import { AdminRole } from "@repo/api-dtos"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import * as React from "react"
import { PageHeader, Section, SectionStack } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import { useAdminRolesContext } from "@/contexts/admin-roles-context"
import { SettingRow, SettingRows } from "@/features/integration-apps/SettingRow"
import { useMcpSettings, useUpdateMcpSettings } from "@/queries/marketplace"

export const Route = createFileRoute("/configuration/mcp")({
	component: McpSwitchesPage,
})

interface Draft {
	cardsEnabled: boolean
	signedOutEnabled: boolean
}

function McpSwitchesPage() {
	const navigate = useNavigate()
	const { hasRole } = useAdminRolesContext()
	const canRead = hasRole(AdminRole.IntegrationsAdmin)
	const { data: settings, isLoading, error } = useMcpSettings()
	const update = useUpdateMcpSettings()
	const [draft, setDraft] = React.useState<Draft | null>(null)

	React.useEffect(() => {
		if (!canRead) navigate({ to: "/unauthorized" })
	}, [canRead, navigate])

	React.useEffect(() => {
		if (settings && draft === null) {
			setDraft({
				cardsEnabled: settings.cardsEnabled,
				signedOutEnabled: settings.signedOutEnabled,
			})
		}
	}, [settings, draft])

	if (!canRead) return null

	const dirty =
		!!settings &&
		!!draft &&
		(draft.cardsEnabled !== settings.cardsEnabled ||
			draft.signedOutEnabled !== settings.signedOutEnabled)

	function save() {
		if (!draft) return
		update.mutate(draft)
	}

	return (
		<div className="flex max-w-4xl flex-col gap-6 p-6">
			<PageHeader
				title="MCP switches"
				description="Staff switches of the MCP server. A saved change reaches every server within about 30 seconds, without a deploy."
			/>
			{error ? (
				<div className="rounded-lg bg-bad-soft px-4 py-3 text-bad">
					Failed to load the switches: {error.message}
				</div>
			) : isLoading || !settings || !draft ? (
				<Skeleton className="h-[200px] w-full rounded-lg" />
			) : (
				<SectionStack>
					<Section label="Switches">
						<SettingRows>
							<SettingRow
								label="MCP cards"
								htmlFor="mcp-cards"
								help="When off, results arrive as text with the same links: no card is shown."
							>
								<Switch
									id="mcp-cards"
									checked={draft.cardsEnabled}
									onCheckedChange={(cardsEnabled) =>
										setDraft({ ...draft, cardsEnabled })
									}
								/>
							</SettingRow>
							<SettingRow
								label="Signed-out mode"
								htmlFor="mcp-signed-out"
								help="When off, a request without a token gets the sign-in challenge. It takes effect only once access is open to everyone."
							>
								<Switch
									id="mcp-signed-out"
									checked={draft.signedOutEnabled}
									onCheckedChange={(signedOutEnabled) =>
										setDraft({ ...draft, signedOutEnabled })
									}
								/>
							</SettingRow>
						</SettingRows>
					</Section>
					<div className="flex items-center justify-end gap-3">
						{settings.updatedAt && (
							<p className="text-xs text-meta">
								Last saved {new Date(settings.updatedAt).toLocaleString()}
								{settings.updatedByUsername
									? ` by ${settings.updatedByUsername}`
									: ""}
							</p>
						)}
						<Button onClick={save} disabled={!dirty || update.isPending}>
							{update.isPending ? "Saving…" : "Save switches"}
						</Button>
					</div>
				</SectionStack>
			)}
		</div>
	)
}
